import React, { useEffect, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridExpandMoreIcon } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import Table from '~/components/v2/common/Table/Table';

import { PromoMeta, PromoRequestFilters } from '~/services/PromoService';

import ColumnSelector from '../PromoTester/ColumnSelector';
import { PromoRequestLoyaltyItemExtended } from '../hooks/usePromoRequests';

import { loyaltyItemCols } from './common';
import { type PageFocusMode, getFocusColumnVisibility } from './pageFocusMode';

interface PromoRequestLoyaltyItemsProps {
  pageFocusMode?: PageFocusMode;
  meta: PromoMeta;
  promoRequestLoyaltyItems?: Array<PromoRequestLoyaltyItemExtended>;
  highlightedRows: Array<string>;
  isLoading: boolean;
  onRowClick?: (params: PromoRequestLoyaltyItemExtended) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  promoRequestsFilter?: PromoRequestFilters;
  setPromoRequestsFilter?: (filter: PromoRequestFilters) => void;
  updateUrlState?: (filter: {
    userId?: string;
    expanded?: boolean;
    itemsExpanded?: boolean;
    logsExpanded?: boolean;
    failuresExpanded?: boolean;
    usersExpanded?: boolean;
  }) => void;
  setFormUserId?: (userId: string) => void;
}

const sharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex'> = {
  sortable: false,
  display: 'flex',
  flex: 1,
};

function getDefaultColumnVisibility(pageFocusMode: PageFocusMode): GridColumnVisibilityModel {
  return {
    codeName: true,
    loyaltyProductType: true,
    resultCode: true,
    promoKey: true,
    loyaltyBonusFilterMethod: true,
    itemId: false,
    offerId: false,
    packageId: false,
    debug: true,
    ...getFocusColumnVisibility(pageFocusMode, { promo: 'codeName' }),
  };
}

function PromoRequestLoyaltyItems({
  pageFocusMode = 'all',
  promoRequestLoyaltyItems,
  isLoading,
  highlightedRows,
  expanded,
  onExpandedChange,
}: PromoRequestLoyaltyItemsProps) {
  // Column visibility state for PromoRequestLoyaltyItems
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() =>
    getDefaultColumnVisibility(pageFocusMode),
  );

  useEffect(() => {
    setColumnVisibilityModel(getDefaultColumnVisibility(pageFocusMode));
  }, [pageFocusMode]);

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: visible,
    }));
  };

  const devColumns: Array<GridColDef<PromoRequestLoyaltyItemExtended>> = [
    {
      field: 'debug',
      headerName: '',
      ...sharedAttrs,
      renderCell: (params) => {
        return (
          <DebugModal
            type="generic"
            data={{
              ...params.row,
            }}
            title={`Loyalty Item - ${params.row.loyaltyProductType} - ${params.row.itemId}`}
          />
        );
      },
    },
  ];

  const columns: Array<GridColDef<PromoRequestLoyaltyItemExtended>> = [...loyaltyItemCols, ...devColumns];

  if (isLoading) {
    return <Spinner size={36} />;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, expanded) => onExpandedChange?.(expanded)}>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        aria-controls="loyalty-items-fields"
        id="loyalty-items-fields"
        sx={{
          backgroundColor: 'action.hover',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <PageSubheader title={`Promo Loyalty Items (${promoRequestLoyaltyItems?.length ?? 0})`} />
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <ColumnSelector
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityChange={handleColumnVisibilityChange}
            />
          </Box>
          <Table
            columns={columns}
            rows={(promoRequestLoyaltyItems ?? []).filter((row) => row != null)}
            paginationMode="client"
            getRowId={(row: PromoRequestLoyaltyItemExtended) => {
              // Ensure we always return a valid string
              if (!row?.uniqueKey) {
                // Fallback if uniqueKey is missing
                const itemId = row?.itemId ?? 'unknown';
                const productType = row?.loyaltyProductType ?? 'unknown';
                const codeName = row?.codeName ?? '';
                return `${codeName}-${itemId}-${productType}`;
              }
              return String(row.uniqueKey);
            }}
            dataGridProps={{
              getRowHeight: () => 'auto',
              getRowClassName: (params) => {
                // For loyalty items, we don't have requestAt/codeName, so we'll use itemId as the key
                const requestKey = params.row.itemId;
                return highlightedRows.includes(requestKey) ? 'highlighted-row' : '';
              },
              columnVisibilityModel,
              onColumnVisibilityModelChange: setColumnVisibilityModel,
              sx: {
                '& .highlighted-row': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              },
            }}
          />
        </Box>
        <Typography variant="subtitle1">
          (This is a display of the loyalty items in the "Recent Request" table above.)
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

export default PromoRequestLoyaltyItems;
